/**
 * Global leaderboard page showing top-rated countries
 * Uses WebSocket for real-time updates
 */

import {
  ChevronLeft,
  Radio,
  TrendingUp,
  Trophy,
  Users,
  WifiOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { getCountryByCode, getRatingColor } from '@/data/countries';
import { useLeaderboardSocket } from '@/hooks/useLeaderboardSocket';

interface LeaderboardPageProps {
  onBack: () => void;
}

export function LeaderboardPage({ onBack }: LeaderboardPageProps) {
  const { leaderboard, summary, connected, error } = useLeaderboardSocket();
  const loading = !connected && leaderboard.length === 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border/60 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-display text-xl sm:text-2xl flex items-center gap-2">
                <Trophy className="w-5 h-5 text-yellow-500" />
                Global Leaderboard
              </h1>
              <p className="text-xs text-muted-foreground hidden sm:block">
                See how countries are rated worldwide
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {connected ? (
              <>
                <Radio className="w-4 h-4 text-green-500 animate-pulse" />
                <span className="hidden sm:inline">Live</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-yellow-500" />
                <span className="hidden sm:inline">Connecting...</span>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Summary stats */}
      {summary && (
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="grid grid-cols-3 gap-4">
            <Card className="p-4 text-center">
              <Users className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
              <div className="text-2xl font-bold">
                {summary.totalUsers.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">Participants</div>
            </Card>
            <Card className="p-4 text-center">
              <TrendingUp className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
              <div className="text-2xl font-bold">
                {summary.totalRatings.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">Total Ratings</div>
            </Card>
            <Card className="p-4 text-center">
              <Trophy className="w-5 h-5 mx-auto text-muted-foreground mb-2" />
              <div className="text-2xl font-bold">{summary.countriesRated}</div>
              <div className="text-xs text-muted-foreground">
                Countries Rated
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <main className="max-w-4xl mx-auto px-4 pb-8">
        {error && (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">Failed to load leaderboard</p>
            <p className="text-xs text-muted-foreground mt-2">
              Retrying automatically...
            </p>
          </Card>
        )}

        {loading && !leaderboard.length && (
          <div className="space-y-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        )}

        {!loading && leaderboard.length === 0 && !error && (
          <Card className="p-8 text-center">
            <Trophy className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-muted-foreground">
              No ratings yet. Be the first to rate countries!
            </p>
          </Card>
        )}

        {leaderboard.length > 0 && (
          <div className="space-y-2">
            {leaderboard.map((entry) => {
              const country = getCountryByCode(entry.countryCode);
              const ratingColor = getRatingColor(
                Math.round(entry.avgRating) as 1 | 2 | 3 | 4 | 5,
              );

              return (
                <Card
                  key={entry.countryCode}
                  className="p-4 flex items-center gap-4 hover:bg-muted/50 transition-colors"
                >
                  {/* Rank */}
                  <div className="w-10 shrink-0 text-center">
                    {entry.rank <= 3 ? (
                      <span className="text-2xl">
                        {entry.rank === 1
                          ? '🥇'
                          : entry.rank === 2
                            ? '🥈'
                            : '🥉'}
                      </span>
                    ) : (
                      <span className="text-lg font-bold text-muted-foreground">
                        #{entry.rank}
                      </span>
                    )}
                  </div>

                  {/* Flag and name */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <span className="text-3xl">{country?.flag || '🏳️'}</span>
                    <div className="min-w-0">
                      <div className="font-semibold truncate">
                        {country?.name || entry.countryCode}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {country?.continent || 'Unknown'}
                      </div>
                    </div>
                  </div>

                  {/* Rating */}
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <div
                        className="text-xl font-bold"
                        style={{ color: ratingColor }}
                      >
                        {entry.avgRating.toFixed(1)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {entry.totalVotes}{' '}
                        {entry.totalVotes === 1 ? 'vote' : 'votes'}
                      </div>
                    </div>

                    {/* Rating bar */}
                    <div className="w-16 h-2 bg-muted rounded-full overflow-hidden hidden sm:block">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${(entry.avgRating / 5) * 100}%`,
                          backgroundColor: ratingColor,
                        }}
                      />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
